// src/components/games/pipsCreatureKeeper/TerritoryList.tsx
import { logger } from '../../../utils/logger';
// FIXED: Added capture limits with premium paywall integration
// FIXED: Removed inline styles for dark mode compatibility

import React, { useState, useMemo } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonSpinner,
  IonBadge,
  IonAlert,
  IonGrid,
  IonRow,
  IonCol,
  IonIcon
} from '@ionic/react';
import { lockClosed, checkmarkCircle } from 'ionicons/icons';
import { Territory, Creature, PlayerCreature, TypeCompatibility, GameData } from '../../../types/pipsCreatureKeeper';
import { supabase } from '../../../services/supabase';
import { captureCreature, addToFieldJournal } from '../../../services/pipsCreatureKeeperService';
import { canAttemptCapture, recordCaptureAttempt, getTimeUntilReset } from '../../../services/captureLimitsService';
import { PremiumPaywall } from '../../../components/PremiumPaywall';
import CaptureSuccess from './CaptureSuccess';
import './TerritoryList.css';

interface TerritoryListProps {
  territories: Territory[];
  availableCreatures: Creature[];
  myCreatures: PlayerCreature[];
  selectedCompanion: Creature | null;
  gameId: string;
  habitatPlacements: { [habitatId: number]: any[] };
  gameData: GameData;
  onRefresh: () => void;
  onNavigateToHabitats: () => void;
}

interface EncounterOption {
  creature: Creature;
  compatibility: number;
}

const TERRITORY_UNLOCK_THRESHOLDS = [
  0, 3, 3, 6, 10, 10, 15, 15, 20, 25, 30
];

const TYPE_COLORS: { [key: string]: string } = {
  flame: '#FF4500',
  tide: '#1E90FF',
  stone: '#696969',
  storm: '#FFA500',
  frost: '#87CEEB',
  bloom: '#32CD32',
  shadow: '#2F2F2F',
  light: '#FFFFE0',
  metal: '#C0C0C0',
  spirit: '#9370DB'
};

// Territory descriptions mapping
const TERRITORY_DESCRIPTIONS: { [key: string]: string } = {
  'Sunrise Meadows': 'Rolling fields of wildflowers and gentle streams where light-loving creatures bask in the dawn.',
  'Mystic Forest': 'An ancient woodland thick with moss and magic, home to creatures of bloom and spirit.',
  'Spirit Gardens': 'Ethereal gardens where the veil between worlds grows thin and ghostly flowers bloom eternally.',
  'Crystal Quarry': 'Glittering caverns of stone and precious gems, carved by creatures of earth and metal.',
  'Volcanic Peaks': 'Smoldering mountains where rivers of lava flow and flame-type creatures thrive in the heat.',
  'Ancient Forge': 'Abandoned metalworks still warm from ancient fires, now claimed by steel and flame dwellers.',
  'Tidal Caves': 'Coastal grottos where saltwater meets stone, echoing with the calls of tide creatures.',
  'Storm Heights': 'Wind-swept plateaus crackling with lightning, where tempest creatures dance among the clouds.',
  'Frozen Falls': 'Crystalline waterfalls frozen mid-cascade, creating an icy wonderland for frost-adapted species.',
  'Shadow Realm': 'A twilight dimension where darkness gathers and shadow creatures slip between the fading light.',
  'Radiant Temple': 'A sacred sanctuary blazing with pure light energy, home to the rarest luminous beings.'
};

const TerritoryList: React.FC<TerritoryListProps> = ({
  territories,
  availableCreatures,
  myCreatures,
  selectedCompanion,
  gameId,
  habitatPlacements,
  gameData,
  onRefresh,
  onNavigateToHabitats
}) => {
  const [exploring, setExploring] = useState(false);
  const [encounterOptions, setEncounterOptions] = useState<EncounterOption[]>([]);
  const [currentTerritory, setCurrentTerritory] = useState<Territory | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [showCaptureSuccess, setShowCaptureSuccess] = useState<{
    show: boolean;
    creatureName: string;
    compatibility: number;
  }>({ show: false, creatureName: '', compatibility: 0 });
  const [resultAlert, setResultAlert] = useState<{
    show: boolean;
    success: boolean;
    message: string;
  }>({ show: false, success: false, message: '' });
  const [blockAlert, setBlockAlert] = useState<{
    show: boolean;
    message: string;
  }>({ show: false, message: '' });
  const [showPremiumPaywall, setShowPremiumPaywall] = useState(false);
  const [captureLimit, setCaptureLimit] = useState<{
    remaining: number;
    resetTime: number;
    isPremium: boolean;
  } | null>(null);

  const [typeCompatibility, setTypeCompatibility] = useState<TypeCompatibility[]>([]);

  React.useEffect(() => {
    const loadTypeCompatibility = async () => {
      const { data, error } = await supabase
        .from('creature_catcher_type_compatibility')
        .select('*');

      if (!error && data) {
        setTypeCompatibility(data);
      }
    };

    loadTypeCompatibility();
  }, []);

  // Load capture limits
  React.useEffect(() => {
    const loadLimits = async () => {
      const limits = await canAttemptCapture();
      setCaptureLimit({
        remaining: limits.remaining,
        resetTime: limits.resetTime,
        isPremium: limits.isPremium
      });
    };

    loadLimits();

    // Refresh every minute to update the timer
    const interval = setInterval(loadLimits, 60000);
    return () => clearInterval(interval);
  }, []);

  const captureCount = myCreatures.length;

  const unplacedCreatures = useMemo(() => {
    const placedIds = new Set<string>();
    Object.values(habitatPlacements).forEach(habitat => {
      habitat.forEach(creature => placedIds.add(creature.id));
    });
    return myCreatures.filter(pc => !placedIds.has(pc.id));
  }, [myCreatures, habitatPlacements]);

  const getTypeColor = (type: string): string => {
    return TYPE_COLORS[type.toLowerCase()] || '#999';
  };

  const getTypeTextColor = (type: string): string => {
    const darkTypes = ['stone', 'shadow', 'spirit'];
    return darkTypes.includes(type.toLowerCase()) ? '#fff' : '#000';
  };

  const getTerritoryDescription = (territory: Territory): string => {
    return TERRITORY_DESCRIPTIONS[territory.name] || 'A mysterious territory waiting to be explored.';
  };

  const getUnlockThreshold = (territory: Territory): number => {
    const sortedTerritories = [...territories].sort((a, b) => a.order_index - b.order_index);
    const index = sortedTerritories.findIndex(t => t.id === territory.id);
    return index >= 0 && index < TERRITORY_UNLOCK_THRESHOLDS.length
      ? TERRITORY_UNLOCK_THRESHOLDS[index]
      : 999;
  };

  const isUnlocked = (territory: Territory): boolean => {
    return captureCount >= getUnlockThreshold(territory);
  };

  const getUnlockedTerritories = (): Territory[] => {
    return territories
      .filter(t => isUnlocked(t))
      .sort((a, b) => a.order_index - b.order_index);
  };

  const getNextTerritory = (): Territory | null => {
    const locked = territories
      .filter(t => !isUnlocked(t))
      .sort((a, b) => a.order_index - b.order_index);
    return locked[0] || null;
  };

  const isTerritoryComplete = (territory: Territory): boolean => {
    const territoryCreatureIds = gameData.creatureTerritories
      .filter(ct => ct.territory_id === territory.id)
      .map(ct => ct.creature_id);

    if (territoryCreatureIds.length === 0) {
      return false;
    }

    const ownedCreatureIds = new Set(myCreatures.map(mc => mc.creature_id));

    for (const creatureId of territoryCreatureIds) {
      if (!ownedCreatureIds.has(creatureId)) {
        return false;
      }
    }

    return true;
  };

  const sortedUnlockedTerritories = useMemo(() => {
    const unlocked = getUnlockedTerritories();
    const incomplete: Territory[] = [];
    const complete: Territory[] = [];

    unlocked.forEach(territory => {
      if (isTerritoryComplete(territory)) {
        complete.push(territory);
      } else {
        incomplete.push(territory);
      }
    });

    return { incomplete, complete };
  }, [territories, captureCount, myCreatures, gameData.creatureTerritories]);

  const calculateCompatibility = (creature: Creature, companion: Creature): number => {
    if (!typeCompatibility || typeCompatibility.length === 0) {
      logger.warn('Type compatibility data not loaded yet');
      return 0.5;
    }

    const creatureTypes = [creature.type1, creature.type2].filter(Boolean);
    const companionTypes = [companion.type1, companion.type2].filter(Boolean);

    let totalMultiplier = 0;
    let comparisons = 0;

    for (const creatureType of creatureTypes) {
      for (const companionType of companionTypes) {
        const compat = typeCompatibility.find(tc => {
          if (!tc.type1 || !tc.type2 || !companionType || !creatureType) return false;
          return tc.type1.toLowerCase() === companionType.toLowerCase() &&
                 tc.type2.toLowerCase() === creatureType.toLowerCase();
        });

        let multiplier = 1.0;
        if (compat) {
          multiplier = parseFloat(compat.compatibility.toString());
        }

        totalMultiplier += multiplier;
        comparisons++;
      }
    }

    const avgMultiplier = comparisons > 0 ? totalMultiplier / comparisons : 1.0;
    const captureChance = Math.min(0.95, 0.25 + (avgMultiplier * 0.375));

    return captureChance;
  };

  const handleExplore = async (territory: Territory) => {
    if (unplacedCreatures.length > 0) {
      setBlockAlert({
        show: true,
        message: `You have ${unplacedCreatures.length} unplaced creature${unplacedCreatures.length > 1 ? 's' : ''}. Please place all creatures in habitats before exploring.`
      });
      return;
    }

    if (!selectedCompanion || exploring) {
      return;
    }

    logger.log('Starting exploration of:', territory.name);
    setExploring(true);
    setCurrentTerritory(territory);

    try {
      const { data: creatureTerritories, error } = await supabase
        .from('creature_catcher_creature_territories')
        .select('creature_id, spawn_weight')
        .eq('territory_id', territory.id);

      if (error) {
        logger.error('Database error:', error);
        throw error;
      }

      if (!creatureTerritories || creatureTerritories.length === 0) {
        const ownedCreatureIds = new Set(myCreatures.map(mc => mc.creature_id));
        const availableForFallback = availableCreatures.filter(c => !ownedCreatureIds.has(c.id));

        if (availableForFallback.length === 0) {
          setResultAlert({
            show: true,
            success: false,
            message: 'You already have all creatures! Amazing work, Keeper!'
          });
          setExploring(false);
          setCurrentTerritory(null);
          return;
        }

        const shuffled = [...availableForFallback].sort(() => Math.random() - 0.5);
        const selectedCreatures = shuffled.slice(0, 3);

        const options: EncounterOption[] = selectedCreatures.map(creature => ({
          creature,
          compatibility: calculateCompatibility(creature, selectedCompanion)
        }));

        setEncounterOptions(options);
        setExploring(false);
        return;
      }

      const ownedCreatureIds = new Set(myCreatures.map(mc => mc.creature_id));

      const availableCreatureIds = creatureTerritories
        .filter(ct => !ownedCreatureIds.has(ct.creature_id))
        .map(ct => ct.creature_id);

      if (availableCreatureIds.length === 0) {
        setResultAlert({
          show: true,
          success: false,
          message: `You already have all creatures from ${territory.name}! Try exploring other territories.`
        });
        setExploring(false);
        setCurrentTerritory(null);
        return;
      }

      const shuffled = [...availableCreatureIds].sort(() => Math.random() - 0.5);
      const selectedIds = shuffled.slice(0, 3);

      const options: EncounterOption[] = selectedIds
        .map(id => {
          const creature = availableCreatures.find(c => c.id === id);
          if (!creature) {
            logger.warn('Creature not found:', id);
            return null;
          }
          return {
            creature,
            compatibility: calculateCompatibility(creature, selectedCompanion)
          };
        })
        .filter((opt): opt is EncounterOption => opt !== null);

      setEncounterOptions(options);
      setExploring(false);
    } catch (error) {
      logger.error('Exploration error:', error);
      setResultAlert({
        show: true,
        success: false,
        message: 'Failed to explore territory. Check console for details.'
      });
      setCurrentTerritory(null);
      setExploring(false);
    }
  };

  const handleAttemptCapture = async (option: EncounterOption) => {
    if (capturing) return;

    // Check capture limits BEFORE attempting
    const limitCheck = await canAttemptCapture();

    if (!limitCheck.allowed) {
      setShowPremiumPaywall(true);
      setEncounterOptions([]);
      setCurrentTerritory(null);
      return;
    }

    logger.log('Attempting to capture:', option.creature.name, 'Compatibility:', (option.compatibility * 100).toFixed(0) + '%');
    setCapturing(true);

    try {
      // Record the attempt BEFORE the capture roll
      await recordCaptureAttempt();

      // Update the UI
      const updatedLimits = await canAttemptCapture();
      setCaptureLimit({
        remaining: updatedLimits.remaining,
        resetTime: updatedLimits.resetTime,
        isPremium: updatedLimits.isPremium
      });

      const success = Math.random() < option.compatibility;
      logger.log('Capture roll:', success ? 'SUCCESS!' : 'FAILED');

      logger.log('Adding to field journal...');
      await addToFieldJournal(gameId, option.creature.id);

      if (success) {
        logger.log('Capturing creature...');
        await captureCreature(gameId, option.creature.id);

        setShowCaptureSuccess({
          show: true,
          creatureName: option.creature.name,
          compatibility: Math.round(option.compatibility * 100)
        });

        setEncounterOptions([]);
        setCurrentTerritory(null);
      } else {
        setResultAlert({
          show: true,
          success: false,
          message: `You encountered ${option.creature.name}!\n\nCapture failed (${(option.compatibility * 100).toFixed(0)}% chance), but it was added to your Field Journal.`
        });

        setEncounterOptions([]);
        setCurrentTerritory(null);
      }
    } catch (error: any) {
      logger.error('Capture error:', error);
      setResultAlert({
        show: true,
        success: false,
        message: error.message || 'Failed to capture creature. Please try again.'
      });
      setEncounterOptions([]);
      setCurrentTerritory(null);
    } finally {
      setCapturing(false);
    }
  };

  const handleCancelEncounter = () => {
    setEncounterOptions([]);
    setCurrentTerritory(null);
  };

  const handleCaptureComplete = async () => {
    setShowCaptureSuccess({ show: false, creatureName: '', compatibility: 0 });
    logger.log('Refreshing game data...');
    await onRefresh();
    onNavigateToHabitats();
  };

  const unlockedTerritories = getUnlockedTerritories();
  const nextTerritory = getNextTerritory();
  const { incomplete: incompleteTerritories, complete: completeTerritories } = sortedUnlockedTerritories;

  return (
    <div className="territory-list-container ion-padding">
      {selectedCompanion && (
        <IonCard className="companion-card-display">
          <IonCardContent className="companion-card-content">
            <div className="companion-display-row">
              <div className="companion-label">
                Companion:
              </div>
              <div className="companion-info-inline">
                <span className="companion-name-text">
                  {selectedCompanion.name}
                </span>
                <IonBadge style={{
                  '--background': getTypeColor(selectedCompanion.type1),
                  color: getTypeTextColor(selectedCompanion.type1)
                }} className="companion-type-badge">
                  {selectedCompanion.type1}
                </IonBadge>
                {selectedCompanion.type2 && (
                  <IonBadge style={{
                    '--background': getTypeColor(selectedCompanion.type2),
                    color: getTypeTextColor(selectedCompanion.type2)
                  }} className="companion-type-badge">
                    {selectedCompanion.type2}
                  </IonBadge>
                )}
              </div>
            </div>
          </IonCardContent>
        </IonCard>
      )}

      {/* Capture attempts indicator - show for free users */}
      {captureLimit && !captureLimit.isPremium && (
        <IonCard className={`capture-limit-card ${captureLimit.remaining > 2 ? 'good' : 'warning'}`}>
          <IonCardContent className="capture-limit-content">
            <div className="capture-limit-display">
              <div className="capture-limit-info">
                <div className="capture-limit-label">
                  Capture Attempts
                </div>
                <div className="capture-limit-count">
                  {captureLimit.remaining} / 5 remaining
                </div>
                <div className="capture-limit-reset">
                  Resets in {getTimeUntilReset(captureLimit.resetTime)}
                </div>
              </div>
              <IonButton
                fill="solid"
                color={captureLimit.remaining > 2 ? 'primary' : 'warning'}
                size="small"
                onClick={() => setShowPremiumPaywall(true)}
                className="get-unlimited-button"
              >
                Get Unlimited
              </IonButton>
            </div>
          </IonCardContent>
        </IonCard>
      )}

      {!selectedCompanion && (
        <IonCard color="warning">
          <IonCardContent className="warning-content">
            <strong>Select a companion from the Habitats tab to explore!</strong>
          </IonCardContent>
        </IonCard>
      )}

      {unplacedCreatures.length > 0 && (
        <IonCard color="danger">
          <IonCardContent className="danger-content">
            <strong>You have {unplacedCreatures.length} unplaced creature{unplacedCreatures.length > 1 ? 's' : ''}!</strong>
            <p className="danger-text">Place all creatures in habitats before exploring.</p>
            <IonButton
              expand="block"
              fill="outline"
              color="light"
              className="go-to-habitats-button"
              onClick={onNavigateToHabitats}
            >
              Go to Habitats
            </IonButton>
          </IonCardContent>
        </IonCard>
      )}

      {encounterOptions.length > 0 && currentTerritory && (
        <IonCard className="encounter-selection-card">
          <IonCardHeader>
            <IonCardTitle className="encounter-selection-title">
              Choose a Creature to Capture
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <p className="encounter-selection-hint">
              Select which creature you would like to attempt to capture in {currentTerritory.name}:
            </p>
            <IonGrid>
              <IonRow>
                {encounterOptions.map((option, index) => (
                  <IonCol size="12" sizeSm="6" sizeMd="4" key={index}>
                    <IonCard
                      button
                      onClick={() => handleAttemptCapture(option)}
                      disabled={capturing}
                      className="encounter-option-card"
                    >
                      <IonCardContent className="encounter-option-content">
                        <div className="encounter-option-ascii">
                          {option.creature.ascii_art || '?'}
                        </div>
                        <h3 className="encounter-option-name">{option.creature.name}</h3>
                        <div className="encounter-option-types">
                          <IonBadge style={{
                            '--background': getTypeColor(option.creature.type1),
                            color: getTypeTextColor(option.creature.type1)
                          }} className="encounter-option-badge">
                            {option.creature.type1}
                          </IonBadge>
                          {option.creature.type2 && (
                            <IonBadge style={{
                              '--background': getTypeColor(option.creature.type2),
                              color: getTypeTextColor(option.creature.type2)
                            }} className="encounter-option-badge">
                              {option.creature.type2}
                            </IonBadge>
                          )}
                        </div>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                ))}
              </IonRow>
            </IonGrid>
            <IonButton
              expand="block"
              fill="outline"
              onClick={handleCancelEncounter}
              disabled={capturing}
              className="cancel-encounter-button"
            >
              Cancel
            </IonButton>
          </IonCardContent>
        </IonCard>
      )}

      {nextTerritory && encounterOptions.length === 0 && (
        <IonCard className="unlock-hint-card">
          <IonCardContent className="unlock-hint-content">
            <strong>
              Capture {getUnlockThreshold(nextTerritory) - captureCount} more creatures to unlock {nextTerritory.name}
            </strong>
          </IonCardContent>
        </IonCard>
      )}

      {encounterOptions.length === 0 && incompleteTerritories.length > 0 && (
        <>
          <h3 className="territory-section-header">Available Territories</h3>
          <div className="territory-grid">
            {incompleteTerritories.map(territory => (
              <IonCard key={territory.id} className="territory-card-item">
                <IonCardHeader>
                  <IonCardTitle className="territory-card-title">
                    {territory.name}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent className="territory-card-content">
                  <p className="territory-card-description">
                    {getTerritoryDescription(territory)}
                  </p>
                  <IonButton
                    expand="block"
                    onClick={() => handleExplore(territory)}
                    disabled={!selectedCompanion || exploring || currentTerritory !== null || unplacedCreatures.length > 0}
                    className="explore-button"
                  >
                    {exploring && currentTerritory?.id === territory.id ? <IonSpinner name="dots" /> : 'Explore'}
                  </IonButton>
                </IonCardContent>
              </IonCard>
            ))}
          </div>
        </>
      )}

      {encounterOptions.length === 0 && completeTerritories.length > 0 && (
        <>
          <h3 className="territory-section-header completed">Completed Territories</h3>
          <div className="territory-grid">
            {completeTerritories.map(territory => (
              <IonCard key={territory.id} className="territory-card-item completed">
                <IonCardHeader>
                  <IonCardTitle className="territory-card-title completed-title">
                    {territory.name}
                    <IonIcon icon={checkmarkCircle} color="success" className="completed-icon" />
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent className="territory-card-content">
                  <p className="territory-card-description">
                    {getTerritoryDescription(territory)}
                  </p>
                  <p className="completed-notice">
                    All creatures captured
                  </p>
                  <IonButton
                    expand="block"
                    fill="outline"
                    disabled
                    className="explore-button completed-button"
                  >
                    Complete
                  </IonButton>
                </IonCardContent>
              </IonCard>
            ))}
          </div>
        </>
      )}

      {encounterOptions.length === 0 && (
        <>
          <h3 className="territory-section-header locked">Locked Territories</h3>
          <div className="territory-grid">
            {territories
              .filter(t => !isUnlocked(t))
              .sort((a, b) => a.order_index - b.order_index)
              .map(territory => (
                <IonCard key={territory.id} className="territory-card-item locked">
                  <IonCardHeader>
                    <IonCardTitle className="territory-card-title locked-title">
                      {territory.name}
                      <IonIcon icon={lockClosed} color="medium" className="locked-icon" />
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent className="territory-card-content">
                    <p className="territory-card-description">
                      {getTerritoryDescription(territory)}
                    </p>
                    <p className="unlock-requirement">
                      Unlock at {getUnlockThreshold(territory)} captures
                      ({getUnlockThreshold(territory) - captureCount} more needed)
                    </p>
                  </IonCardContent>
                </IonCard>
              ))}
          </div>
        </>
      )}

      <IonAlert
        isOpen={blockAlert.show}
        onDidDismiss={() => setBlockAlert({ show: false, message: '' })}
        header="Cannot Explore"
        message={blockAlert.message}
        buttons={[
          {
            text: 'Go to Habitats',
            handler: () => {
              onNavigateToHabitats();
            }
          },
          {
            text: 'OK',
            role: 'cancel'
          }
        ]}
      />

      <IonAlert
        isOpen={resultAlert.show}
        onDidDismiss={() => setResultAlert({ show: false, success: false, message: '' })}
        header={resultAlert.success ? 'Capture Successful!' : 'Discovery!'}
        message={resultAlert.message}
        buttons={['OK']}
      />

      {showCaptureSuccess.show && (
        <CaptureSuccess
          creatureName={showCaptureSuccess.creatureName}
          compatibility={showCaptureSuccess.compatibility}
          onComplete={handleCaptureComplete}
        />
      )}

      {/* Premium Paywall */}
      <PremiumPaywall
        isOpen={showPremiumPaywall}
        onDismiss={() => setShowPremiumPaywall(false)}
        feature="capture_attempts"
      />
    </div>
  );
};

export default TerritoryList;